import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MarketingFood } from '@shared/models/dataref.model';

@Component({
  selector: 'app-marketing-food-form-field',
  templateUrl: './marketing-food-form-field.component.html',
  styleUrls: ['./marketing-food-form-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MarketingFoodFormFieldComponent {
  @Input() foodFormControl?: UntypedFormControl;
  @Input() requestedFoodFormControl?: UntypedFormControl;
  @Input() suggestedMarketingFoods: string[] = [];
  @Input() marketingFoods: MarketingFood[] = [];

  inputValue = '';

  constructor(private readonly changeDetector: ChangeDetectorRef) {}

  /**
   * Triggered when the user input value changes
   *
   * @param value the new user input value
   */
  inputValueChange(value?: string): void {
    this.inputValue = value ?? '';
    this.changeDetector.detectChanges();
  }

  /**
   * Checks whether to display the 'food' or 'requestedFood' field
   *
   * @returns true if we should display 'requestedFood', false if we should display 'food'
   */
  requestedFoodMode(): boolean {
    return !this.foodFormControl?.value?.length && !!this.requestedFoodFormControl?.value?.length;
  }

  /**
   * Returns the correct formControl to be used based on the current values combination
   */
  getFormControl(): UntypedFormControl {
    if (this.requestedFoodMode() && this.requestedFoodFormControl) {
      return this.requestedFoodFormControl;
    }
    return this.foodFormControl ?? new UntypedFormControl();
  }

  /**
   * Finds the right ref data based on the given key and returns the translated value
   *
   * @param inputKey the key to be searched for in the ref data list
   * @returns the translated value if found, or the key itself if not
   */
  getMarketingFoodText(inputKey: string): string {
    return this.marketingFoods.find((mFood) => mFood.key === inputKey)?.translatedName ?? inputKey;
  }

  /**
   * Called when the user clicks a suggested food to add it
   *
   * @param suggestedFood the clicked suggested food key
   */
  selectFood(suggestedFood: string): void {
    this.foodFormControl?.setValue(suggestedFood);
    this.foodFormControl?.markAsDirty();
    this.requestedFoodFormControl?.setValue(null);
    this.requestedFoodFormControl?.markAsDirty();
  }
}
